import { useCallback, useMemo, useState } from 'react';
import {
  syncCatalogSnapshotAfterLocalWrite,
  syncCatalogSnapshotFromRemote,
} from '../../catalog/infra/catalogSnapshotSync';
import { upsertWebCatalogPriceListInCache } from '../../catalog/infra/catalogRamSnapshotCache';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import type { PriceList, PriceListUpsertInput, PriceListWithItems } from '../domain/priceListTypes';
import { getPriceListByIdLocal, listPriceListsLocal, listPriceListItemCountsLocal } from '../infra/priceListsRepoRead';
import { applyPriceListRemote } from '../infra/priceListsApi';
import { matchesSearchAny } from '../../shared/format/searchText';

function sortPriceLists(rows: PriceList[]): PriceList[] {
  return [...rows].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

export function usePriceListsListScreen() {
  const branchId = useSessionStore((s) => s.currentBranchId);
  const canView = useModulePermission('PriceLists', 'view');
  const canEdit = useModulePermission('PriceLists', 'edit');
  const [lists, setLists] = useState<PriceList[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<PriceListWithItems | null>(null);
  const [itemCounts, setItemCounts] = useState<Map<string, number>>(new Map());

  const loadFn = useCallback(async () => {
    setLoadError(null);
    try {
      await syncCatalogSnapshotFromRemote({ force: true, forceFull: true });
      const rows = await listPriceListsLocal();
      setLists(rows);
      setItemCounts(await listPriceListItemCountsLocal());
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'No pudimos cargar las listas.');
    }
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return lists;
    return lists.filter((l) => matchesSearchAny([l.name, l.code], search));
  }, [lists, search]);

  const onCreate = useCallback(() => {
    setEditing(null);
    setEditorOpen(true);
  }, []);

  const onEdit = useCallback(async (list: PriceList) => {
    const full = await getPriceListByIdLocal(list.id);
    setEditing(full ?? { ...list, items: [] });
    setEditorOpen(true);
  }, []);

  const saveList = useCallback(
    async (input: PriceListUpsertInput, existingId?: string | null) => {
      if (!branchId) throw new Error('Elegí una sucursal.');
      const id = existingId ?? crypto.randomUUID();
      const now = new Date().toISOString();
      await applyPriceListRemote(existingId ? 'update' : 'insert', {
        id,
        branchId,
        operationId: crypto.randomUUID(),
        input,
        createdAt: now,
        updatedAt: now,
      });

      const orgId = lists.find((l) => l.id === id)?.organizationId ?? lists[0]?.organizationId ?? '';
      const optimistic: PriceList = {
        id,
        organizationId: orgId,
        name: input.name,
        code: input.code ?? null,
        isDefault: Boolean(input.isDefault),
        appliesOnline: Boolean(input.appliesOnline),
        isActive: input.isActive !== false,
        pricingMode: input.pricingMode ?? 'fixed',
        createdAt: existingId ? lists.find((l) => l.id === id)?.createdAt ?? now : now,
        updatedAt: now,
        deletedAt: null,
      };

      const cacheItems = (input.items ?? []).map((item) => ({
        // UUID real: el server inserta el id verbatim — un id sintético
        // (`${id}-item-N`) falla el `::uuid` cast del RPC al re-guardar.
        id: item.id ?? crypto.randomUUID(),
        price_list_id: id,
        product_id: item.productId,
        variant_id: item.variantId ?? null,
        price: item.price ?? null,
        percent_off: item.percentOff ?? null,
        min_qty: item.minQty ?? null,
        created_at: now,
        updated_at: now,
      }));

      upsertWebCatalogPriceListInCache(
        {
          id,
          organization_id: orgId,
          name: input.name,
          code: input.code ?? null,
          is_default: Boolean(input.isDefault),
          applies_online: Boolean(input.appliesOnline),
          is_active: input.isActive !== false,
          pricing_mode: input.pricingMode ?? 'fixed',
          created_at: optimistic.createdAt,
          updated_at: now,
          deleted_at: null,
        },
        cacheItems,
      );
      setLists((prev) => sortPriceLists([...prev.filter((l) => l.id !== id), optimistic]));
      setItemCounts((prev) => new Map(prev).set(id, cacheItems.length));

      // Sync en background: no bloquear el toast ni el cierre del modal.
      // Post-effect best-effort (server-authoritative command ya confirmó el write).
      void syncCatalogSnapshotAfterLocalWrite()
        .then(async () => {
          const rows = await listPriceListsLocal();
          if (!rows.some((row) => row.id === id)) {
            upsertWebCatalogPriceListInCache(
              {
                id,
                organization_id: orgId,
                name: input.name,
                code: input.code ?? null,
                is_default: Boolean(input.isDefault),
                applies_online: Boolean(input.appliesOnline),
                is_active: input.isActive !== false,
                pricing_mode: input.pricingMode ?? 'fixed',
                created_at: optimistic.createdAt,
                updated_at: now,
                deleted_at: null,
              },
              cacheItems,
            );
            setLists(sortPriceLists([...rows.filter((row) => row.id !== id), optimistic]));
            return;
          }
          setLists(rows);
        })
        .catch(() => {
          // Optimistic ya está en UI; el próximo refresh completa.
        });
    },
    [branchId, lists],
  );

  return {
    canView,
    canEdit,
    lists: filtered,
    /** Total sin filtrar: distingue "sin listas" de "sin resultados" (#1459). */
    totalCount: lists.length,
    itemCounts,
    loadError,
    search,
    setSearch,
    loadFn,
    editorOpen,
    setEditorOpen,
    editing,
    onCreate,
    onEdit,
    saveList,
  };
}
